import { Injectable } from "@angular/core";
import { OnPremiseSyncDataState } from "./on-premise-sync-state.service";
import { concatMap, forkJoin, from, map, mergeMap, Observable, of, tap, toArray, zip } from "rxjs";
import { toInspectionImageCreate } from "../sync-mapper";
import { InspectionImageDbService } from "../../database/inspection-image-db.service";
import { InspectionImageService } from "../../http-api/inspection-image.service";
import { FileSystemAccessService } from "../../file-system-access.service";
import { InspectionDbService } from "../../database/inspection-db.service";
import { ImageUploadService } from "../../image-upload.service";
import { InspectionImageCreate, InspectionImageInfo } from "../../../_objects/inspection-image";
import { InspectionImageSchema } from "../../../_offline/models/inspectionImage.schema";
import { OfflineUtilsService } from "../../../_offline/services/offline-utils.service";
import { USE_IMAGE_TYPE } from "../../../app.constants";
import { OfflineManagementService } from "../../offline-management.service";
import { AnnotationImageService } from "../../http-api/annotation-image.service";
import { AnnotationObjectForUpdate } from "../../../_objects/inspection-image-annotation";

@Injectable({
  providedIn: "root"
})
export class InspectionImageFirstSyncService {
  constructor(
    private syncState: OnPremiseSyncDataState,
    private inspectionDbService: InspectionDbService,
    private inspectionImageDbService: InspectionImageDbService,
    private inspectionImageService: InspectionImageService,
    private fileSystemAccessService: FileSystemAccessService,
    private uploadService: ImageUploadService
  ) {}

  createInspectionImages(): Observable<InspectionImageInfo[]> {
    return this.inspectionDbService.getAllOnPremise().pipe(
      concatMap((inspection) => inspection),
      mergeMap((inspection) => this.createImagesForInspection(inspection.id)),
      toArray()
    );
  }

  private createImagesForInspection = (inspectionId: string): Observable<InspectionImageInfo> => {
    return this.inspectionImageDbService.getAllOnPremise(inspectionId).pipe(
      mergeMap((imageSchemas) =>
        from(imageSchemas).pipe(
          mergeMap((imageSchema) => this.combineLocalFileAndSchema(imageSchema)),
          map(([file, imageSchema]) => toInspectionImageCreate(imageSchema, this.syncState.getInspectionId(inspectionId), file)),
          toArray(),
          mergeMap((imageCreations) => this.createAndUploadImages(inspectionId, imageCreations, imageSchemas))
        )
      )
    );
  };

  private combineLocalFileAndSchema(schema: InspectionImageSchema): Observable<[File, InspectionImageSchema]> {
    return zip(from(this.fileSystemAccessService.getFile(schema.localFilePath, schema.fileName, schema.mimeType)), of(schema));
  }

  private createAndUploadImages(
    inspectionId: string,
    imageCreations: InspectionImageCreate[],
    imageSchemas: InspectionImageSchema[]
  ): Observable<any> {
    return this.inspectionImageService.createInspectionImages(this.syncState.getInspectionId(inspectionId), imageCreations).pipe(
      tap((response) => {
        response.createdImages.forEach((imageInfo) => {
          const imageFromSchema = this.associateLocalImageWithCreated(imageSchemas, imageInfo);

          if (imageFromSchema) {
            this.syncState.updateInspectionImage(imageInfo.imageId, imageFromSchema.id);
          }
        });
      }),
      mergeMap((response) => this.uploadService.uploadImageToBlobUrl(imageCreations, response.createdImages))
    );
  }

  private associateLocalImageWithCreated(imageSchemas: InspectionImageSchema[], inspectionImage: InspectionImageInfo) {
    return imageSchemas.find((img) => img.fileName === inspectionImage.originalFileName && img.subjectLocationId == inspectionImage.folderName);
  }
}
